<template>
  <content-subpage
    class="whitelist-website-details-content-subpage"
    :visible="visible"
    :titles="titles"
    :show-content-title="false"
    @content-subpage-hidden="actionHidden"
  >
    <!-- Left panel -->
    <template #main-content>
      <match-indicator v-if="matchWebsiteName" />
      <div class="info-header">
        <ellipsis-text class="website-name" :text="websiteName" />
      </div>
      <inline-loader v-if="isLoading" />
      <cv-form v-else class="website-details-left-panel">
        <!-- External link -->
        <div v-if="website.homeUrl" class="domain-url">
          <button class="openInNewTab action" @click.prevent="handleOpenWebsite">
            <svg-icon name="ExternalLink32" kind="action" />
          </button>
        </div>
        <!-- Icon -->
        <dl v-if="website.iconUrl" class="field-set icon">
          <dd class="field-value">
            <div class="image-placeholder">
              <img :src="getIconUrl" class="thumbnail" />
            </div>
          </dd>
        </dl>
        <!-- Description -->
        <match-indicator v-if="matchDescription" />
        <dl class="field-set description">
          <dd class="field-value">{{ website.description }}</dd>
        </dl>
      </cv-form>
    </template>

    <!-- Right panel -->
    <template #sub-content>
      <inline-loader v-if="isLoading" />
      <cv-form v-else class="website-details-right-panel">
        <!-- Domains -->
        <dl class="field-set domains">
          <dd class="field-value">
            <div v-for="(domain, index) in domains" :key="index" class="domain">
              {{ domain.domain }}
            </div>
          </dd>
        </dl>
        <!-- Keywords -->
        <match-indicator v-if="matchKeyword" />
        <dl class="field-set keywords">
          <dt class="field-label">{{ t("website_keyword_tags") }}</dt>
          <dd v-if="showKeywords" class="field-value">
            <keywords :tags="website.keywords" />
          </dd>
          <dd v-else class="output-text">
            {{ emptyKeywordLabel }}
          </dd>
        </dl>
      </cv-form>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import WhitelistWebsiteApi from "@apis/whitelist.website.api.ts";
import { loadLocaleMessages } from "@plugins/i18n.ts";
import { isEmpty } from "@utils/var.check.ts";
import {
  WhitelistDomainModel,
  WhitelistWebsiteDetailModel,
  SearchHighlightModel,
} from "@modules/whitelist-website/types.ts";
import Keywords from "@plugins/keywords/components/Keywords.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import MatchIndicator from "@plugins/common/components/MatchIndicator.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";

interface Props {
  params: {
    baseTitle: string;
    websiteUuid: string;
    highlights?: SearchHighlightModel;
  };
}

// Props
const { params } = defineProps<Props>();

// Data
const website = ref<WhitelistWebsiteDetailModel>({} as WhitelistWebsiteDetailModel);
const domains = ref<WhitelistDomainModel[]>([]);
const isLoading = ref(true);
const visible = ref(true);
const emptyKeywordLabel = ref("-");

// Composable
const { t } = useI18n();
const router = useRouter();
const { titles, actionHidden, defaultExpose } = useContentSubpage(params);
const { passiveApiFailHandler } = useErrorHandler();

// Computed
const websiteName = computed(() => {
  return params.baseTitle[0];
});

const getIconUrl = computed(() => {
  return `${website.value.iconUrl}?t=${Date.now()}`;
});

const showKeywords = computed(() => {
  return website.value.keywords.length > 0;
});

const matchDescription = computed(() => {
  return isEmpty(params?.highlights) ? null : params?.highlights?.description;
});

const matchKeyword = computed(() => {
  return isEmpty(params?.highlights) ? null : params?.highlights?.keyword;
});

const matchWebsiteName = computed(() => {
  return !isEmpty(params?.highlights) && !isEmpty(params?.highlights?.name);
});

// Methods
const handleOpenWebsite = (): void => {
  window.open(website.value.homeUrl, "_blank");
};

const sortDomains = (domains: WhitelistDomainModel[]): WhitelistDomainModel[] => {
  return domains.sort((a, b) => {
    const fa = a.domain.toLowerCase();
    const fb = b.domain.toLowerCase();

    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    return 0;
  });
};

// Created
onMounted(() => {
  const localeMessages = loadLocaleMessages("whitelist-website");
  const loadWebsite = WhitelistWebsiteApi.fetchWhitelistWebsiteDetails(params.websiteUuid).then(
    (response) => {
      if (response.code !== "ok") {
        router.push({ name: "Forbidden" });
        return;
      }
      website.value = response.data;
      domains.value = sortDomains(website.value.domains);
    },
    passiveApiFailHandler,
  );

  Promise.all([localeMessages, loadWebsite]).then(() => {
    isLoading.value = false;
  });
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/abstracts/mixins";

.whitelist-website-details-content-subpage {
  .info-header {
    @include carbon--type-style("productive-heading-02", true);
    max-width: calc(100% - $spacing-09);
  }

  .website-details-left-panel {
    .domain-url {
      position: absolute;
      top: 0.75rem;
      right: 1rem;

      .sls-icon {
        [fill] {
          fill: $interactive-01;
        }
      }
    }

    .field-set {
      &.description {
        .field-value {
          @include carbon--type-style("body-short-01");
        }
      }

      &.icon {
        .image-placeholder {
          display: flex;
          justify-content: center;
          @include aspect-ratio(1);

          img {
            height: 100%;
            object-fit: cover;
          }
        }
      }
    }
  }

  .website-details-right-panel {
    .domain {
      @include carbon--type-style("label-01", true);
      margin-bottom: $spacing-05;
      padding: $spacing-03;
      border-radius: 10px;
      background-color: $ui-background;
      @include media("tablet-max") {
        background-color: $ui-01;
      }
    }
  }
}
</style>
